import { useMemo } from 'react'

interface BarGridColumn {
  id: string
  label: string
  shortLabel?: string
  color: string
}

interface BarGridRow {
  id: string
  label: string
  values: Record<string, number> // columnId -> percentage
}

interface BarGridProps {
  columns: BarGridColumn[]
  rows: BarGridRow[]
  title?: React.ReactNode
  description?: string
  maxValue?: number // For scaling bars (default: auto from data)
  colorIntensity?: boolean // Vary color intensity based on value
}

export function BarGrid({
  columns,
  rows,
  title,
  description,
  maxValue: providedMaxValue,
  colorIntensity = true,
}: BarGridProps) {
  // Calculate max value for scaling
  const maxValue = useMemo(() => {
    if (providedMaxValue) return providedMaxValue
    let max = 0
    rows.forEach(row => {
      columns.forEach(col => {
        const val = row.values[col.id] || 0
        if (val > max) max = val
      })
    })
    return max || 100
  }, [rows, columns, providedMaxValue])

  // Adjust color opacity based on value (darker = higher)
  const getBarColor = (baseColor: string, value: number) => {
    if (!colorIntensity) return baseColor
    // Scale opacity from 0.4 to 1.0 based on value relative to max
    const opacity = 0.4 + (value / maxValue) * 0.6
    return baseColor + Math.round(opacity * 255).toString(16).padStart(2, '0')
  }

  if (rows.length === 0 || columns.length === 0) {
    return (
      <div className="text-slate-400 dark:text-slate-500 text-sm italic">
        No data available
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Title and description */}
      {title && (
        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
          {title}
        </h3>
      )}
      {description && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {description}
        </p>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 pb-2 border-b border-slate-100 dark:border-slate-700">
        {columns.map(col => (
          <div key={col.id} className="flex items-center gap-1.5 text-xs">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: col.color }}
            />
            <span className="text-slate-600 dark:text-slate-400">
              {col.label}
            </span>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="overflow-hidden">
        <table className="w-full table-fixed border-collapse text-sm border border-slate-200 dark:border-slate-600">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-700/50">
              <th className="text-left text-xs font-medium text-slate-500 dark:text-slate-400 py-2 px-2 border-b border-r border-slate-200 dark:border-slate-600 w-40">
                {/* Row label column */}
              </th>
              {columns.map((col, idx) => (
                <th
                  key={col.id}
                  className={`text-left text-xs font-medium text-slate-500 dark:text-slate-400 py-2 px-1 border-b border-slate-200 dark:border-slate-600 ${idx < columns.length - 1 ? 'border-r' : ''}`}
                  title={col.label}
                >
                  <div className="text-[10px] leading-tight">
                    {col.shortLabel || col.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={row.id} className={`${rowIdx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50/50 dark:bg-slate-700/30'} ${rowIdx < rows.length - 1 ? 'border-b border-slate-200 dark:border-slate-600' : ''}`}>
                <td className="text-xs text-slate-600 dark:text-slate-400 py-2 px-2 font-medium border-r border-slate-200 dark:border-slate-600 leading-tight" title={row.label}>
                  {row.label}
                </td>
                {columns.map((col, colIdx) => {
                  const value = row.values[col.id] || 0
                  const barWidth = (value / maxValue) * 100

                  return (
                    <td key={col.id} className={`py-1.5 px-1 ${colIdx < columns.length - 1 ? 'border-r border-slate-100 dark:border-slate-700' : ''}`}>
                      <div className="flex items-center gap-1">
                        <div
                          className="h-5 rounded-sm flex items-center"
                          style={{
                            width: `${Math.max(barWidth, 8)}%`,
                            backgroundColor: getBarColor(col.color, value),
                          }}
                          title={`${col.label}: ${Math.round(value)}%`}
                        />
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                          {Math.round(value)}%
                        </span>
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
